import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash2, Plus, Car, Download, Upload, FileSpreadsheet, Plane } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface TransferRoute {
  id: string;
  travel_area: string;
  display_name: string;
  price_standard: number;
  price_vip: number;
  per_pax: number;
  per_pax_vip: number;
  max_passenger_standard: number;
  max_passenger_vip: number;
  airport: string;
}

const AdminTransfers = () => {
  const [routes, setRoutes] = useState<TransferRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState<'punta_cana_airport' | 'la_romana_airport' | 'las_americas_airport' | 'la_isabela_airport' | 'puerto_plata_airport' | 'samana_airport' | 'santiago_airport'>('punta_cana_airport');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<TransferRoute | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [formData, setFormData] = useState({
    travel_area: '',
    display_name: '',
    price_standard: 0,
    price_vip: 0,
    per_pax: 0,
    per_pax_vip: 0,
    max_passenger_standard: 4,
    max_passenger_vip: 5,
  });
  const [enabledAirports, setEnabledAirports] = useState<string[]>([]);
  const [savingAirports, setSavingAirports] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const airports = [
    { value: 'punta_cana_airport', label: 'Punta Cana Airport (PUJ)' },
    { value: 'la_romana_airport', label: 'La Romana Airport (LRM)' },
    { value: 'las_americas_airport', label: 'Las Americas Airport (SDQ)' },
    { value: 'la_isabela_airport', label: 'La Isabela Airport (JBQ)' },
    { value: 'puerto_plata_airport', label: 'Puerto Plata Airport (POP)' },
    { value: 'samana_airport', label: 'Samana El Catey Airport (AZS)' },
    { value: 'santiago_airport', label: 'Santiago Airport (STI)' },
  ];

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(selectedAirport)
        .select('*')
        .order('display_name');

      if (error) throw error;
      setRoutes(data || []);
    } catch (error) {
      console.error('Error fetching routes:', error);
      toast({
        title: "Error",
        description: "Failed to fetch transfer routes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, [selectedAirport]);

  useEffect(() => {
    fetchEnabledAirports();
  }, []);

  const fetchEnabledAirports = async () => {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('value')
        .eq('key', 'enabled_airports')
        .single();

      if (error) {
        // Default all enabled
        setEnabledAirports(airports.map(a => a.value));
        return;
      }

      const value = data?.value;
      if (Array.isArray(value)) {
        setEnabledAirports(value as string[]);
      } else if (typeof value === 'string') {
        try {
          setEnabledAirports(JSON.parse(value));
        } catch {
          setEnabledAirports(airports.map(a => a.value));
        }
      } else {
        setEnabledAirports(airports.map(a => a.value));
      }
    } catch {
      setEnabledAirports(airports.map(a => a.value));
    }
  };

  const toggleAirport = async (airportId: string, enabled: boolean) => {
    const updated = enabled
      ? [...enabledAirports, airportId]
      : enabledAirports.filter(id => id !== airportId);

    setEnabledAirports(updated);
    setSavingAirports(true);

    try {
      const { error } = await supabase
        .from('site_config')
        .upsert({ key: 'enabled_airports', value: updated as any }, { onConflict: 'key' });

      if (error) throw error;

      toast({
        title: "Saved",
        description: `Airport ${enabled ? 'enabled' : 'disabled'} successfully`,
      });
    } catch (error) {
      console.error('Error saving airport config:', error);
      // Revert
      setEnabledAirports(prev => enabled
        ? prev.filter(id => id !== airportId)
        : [...prev, airportId]
      );
      toast({
        title: "Error",
        description: "Failed to update airport settings",
        variant: "destructive",
      });
    } finally {
      setSavingAirports(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingRoute) {
        // Update existing route
        const { error } = await supabase
          .from(selectedAirport)
          .update(formData)
          .eq('id', editingRoute.id);

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Transfer route updated successfully",
        });
      } else {
        // Create new route
        const { error } = await supabase
          .from(selectedAirport)
          .insert([{ ...formData, airport: selectedAirport }]);

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Transfer route created successfully",
        });
      }

      setIsDialogOpen(false);
      resetForm();
      fetchRoutes();
    } catch (error) {
      console.error('Error saving route:', error);
      toast({
        title: "Error",
        description: "Failed to save transfer route",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (route: TransferRoute) => {
    setEditingRoute(route);
    setFormData({
      travel_area: route.travel_area,
      display_name: route.display_name,
      price_standard: route.price_standard,
      price_vip: route.price_vip,
      per_pax: route.per_pax,
      per_pax_vip: route.per_pax_vip,
      max_passenger_standard: route.max_passenger_standard,
      max_passenger_vip: route.max_passenger_vip,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this route?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from(selectedAirport)
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Transfer route deleted successfully",
      });
      
      fetchRoutes();
    } catch (error) {
      console.error('Error deleting route:', error);
      toast({
        title: "Error",
        description: "Failed to delete transfer route",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingRoute(null);
    setFormData({
      travel_area: '',
      display_name: '',
      price_standard: 0,
      price_vip: 0,
      per_pax: 0,
      per_pax_vip: 0,
      max_passenger_standard: 4,
      max_passenger_vip: 5,
    });
  };

  const exportTemplate = () => {
    const templateData = [
      {
        travel_area: 'bavaro',
        display_name: 'Bavaro Beach Resort',
        price_standard: 45.00,
        price_vip: 65.00,
        per_pax: 5,
        per_pax_vip: 8,
        max_passenger_standard: 4,
        max_passenger_vip: 5
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transfer Routes');

    // Add some styling and column widths
    ws['!cols'] = [
      { wch: 15 }, // travel_area
      { wch: 25 }, // display_name
      { wch: 15 }, // price_standard
      { wch: 12 }, // price_vip
      { wch: 10 }, // per_pax
      { wch: 12 }, // per_pax_vip
      { wch: 20 }, // max_passenger_standard
      { wch: 18 }  // max_passenger_vip
    ];

    const airportName = airports.find(a => a.value === selectedAirport)?.label || selectedAirport;
    const fileName = `transfer_routes_template_${selectedAirport}.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    
    toast({
      title: "Template Downloaded",
      description: `Excel template for ${airportName} has been downloaded`,
    });
  };

  const importFromExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportProgress(0);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        throw new Error('Excel file is empty');
      }

      // Validate required columns
      const requiredColumns = ['travel_area', 'display_name', 'price_standard', 'price_vip', 'per_pax', 'per_pax_vip', 'max_passenger_standard', 'max_passenger_vip'];
      const firstRow = jsonData[0] as any;
      const missingColumns = requiredColumns.filter(col => !(col in firstRow));
      
      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      const validatedData = [];
      const errors = [];

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i] as any;
        setImportProgress((i / jsonData.length) * 50);

        try {
          // Validate and clean data
          const cleanedRow = {
            travel_area: String(row.travel_area || '').trim(),
            display_name: String(row.display_name || '').trim(),
            price_standard: parseFloat(row.price_standard) || 0,
            price_vip: parseFloat(row.price_vip) || 0,
            per_pax: parseInt(row.per_pax) || 0,
            per_pax_vip: parseInt(row.per_pax_vip) || 0,
            max_passenger_standard: parseInt(row.max_passenger_standard) || 4,
            max_passenger_vip: parseInt(row.max_passenger_vip) || 5,
            airport: selectedAirport
          };

          // Validate required fields
          if (!cleanedRow.travel_area || !cleanedRow.display_name) {
            errors.push(`Row ${i + 2}: travel_area and display_name are required`);
            continue;
          }

          // Validate numeric values
          if (cleanedRow.price_standard < 0 || cleanedRow.price_vip < 0) {
            errors.push(`Row ${i + 2}: Prices cannot be negative`);
            continue;
          }

          validatedData.push(cleanedRow);
        } catch (error) {
          errors.push(`Row ${i + 2}: ${error}`);
        }
      }

      if (errors.length > 0 && validatedData.length === 0) {
        throw new Error(`All rows have errors:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? '\n...' : ''}`);
      }

      // Check for duplicates in current data
      const existingRoutes = new Set(routes.map(route => route.travel_area.toLowerCase()));
      const duplicates = validatedData.filter(item => existingRoutes.has(item.travel_area.toLowerCase()));
      
      if (duplicates.length > 0) {
        const proceed = confirm(`Found ${duplicates.length} routes that already exist. Do you want to skip them and import the rest?`);
        if (!proceed) {
          setImporting(false);
          return;
        }
        // Remove duplicates
        const uniqueData = validatedData.filter(item => !existingRoutes.has(item.travel_area.toLowerCase()));
        validatedData.splice(0, validatedData.length, ...uniqueData);
      }

      if (validatedData.length === 0) {
        throw new Error('No valid data to import after removing duplicates');
      }

      // Import data in batches
      const batchSize = 10;
      let imported = 0;
      
      for (let i = 0; i < validatedData.length; i += batchSize) {
        const batch = validatedData.slice(i, i + batchSize);
        
        const { error } = await supabase
          .from(selectedAirport)
          .insert(batch);

        if (error) throw error;
        
        imported += batch.length;
        setImportProgress(50 + (imported / validatedData.length) * 50);
      }

      toast({
        title: "Import Successful",
        description: `Imported ${imported} routes successfully${errors.length > 0 ? `. ${errors.length} rows had errors.` : ''}`,
      });

      if (errors.length > 0) {
        console.warn('Import errors:', errors);
      }

      fetchRoutes();
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import Excel file",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
      setImportProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Transfer Management</h1>
            <p className="text-muted-foreground">
              Manage transfer routes and pricing for all airports
            </p>
          </div>

        {/* Active Airports Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="h-5 w-5" />
              Active Airports
            </CardTitle>
            <CardDescription>
              Enable or disable airports shown in the public booking form
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {airports.map((airport) => (
                <div key={airport.value} className="flex items-center justify-between rounded-lg border p-3">
                  <Label htmlFor={`airport-${airport.value}`} className="text-sm font-medium cursor-pointer flex-1">
                    {airport.label}
                  </Label>
                  <Switch
                    id={`airport-${airport.value}`}
                    checked={enabledAirports.includes(airport.value)}
                    onCheckedChange={(checked) => toggleAirport(airport.value, checked)}
                    disabled={savingAirports}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="mr-2 h-4 w-4" />
                Add New Route
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>
                  {editingRoute ? 'Edit Transfer Route' : 'Add New Transfer Route'}
                </DialogTitle>
                <DialogDescription>
                  Configure the transfer route details and pricing for {airports.find(a => a.value === selectedAirport)?.label}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="travel_area">Travel Area</Label>
                      <Input
                        id="travel_area"
                        value={formData.travel_area}
                        onChange={(e) => setFormData(prev => ({ ...prev, travel_area: e.target.value }))}
                        placeholder="e.g., bavaro"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="display_name">Display Name</Label>
                      <Input
                        id="display_name"
                        value={formData.display_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                        placeholder="e.g., Bavaro Beach"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="price_standard">Standard Price ($)</Label>
                      <Input
                        id="price_standard"
                        type="number"
                        step="0.01"
                        value={formData.price_standard}
                        onChange={(e) => setFormData(prev => ({ ...prev, price_standard: parseFloat(e.target.value) || 0 }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="price_vip">VIP Price ($)</Label>
                      <Input
                        id="price_vip"
                        type="number"
                        step="0.01"
                        value={formData.price_vip}
                        onChange={(e) => setFormData(prev => ({ ...prev, price_vip: parseFloat(e.target.value) || 0 }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="per_pax">Extra Cost per Pax - Standard ($)</Label>
                      <Input
                        id="per_pax"
                        type="number"
                        value={formData.per_pax}
                        onChange={(e) => setFormData(prev => ({ ...prev, per_pax: parseInt(e.target.value) || 0 }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="per_pax_vip">Extra Cost per Pax - VIP ($)</Label>
                      <Input
                        id="per_pax_vip"
                        type="number"
                        value={formData.per_pax_vip}
                        onChange={(e) => setFormData(prev => ({ ...prev, per_pax_vip: parseInt(e.target.value) || 0 }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="max_passenger_standard">Max Passengers - Standard</Label>
                      <Input
                        id="max_passenger_standard"
                        type="number"
                        value={formData.max_passenger_standard}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_passenger_standard: parseInt(e.target.value) || 4 }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="max_passenger_vip">Max Passengers - VIP</Label>
                      <Input
                        id="max_passenger_vip"
                        type="number"
                        value={formData.max_passenger_vip}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_passenger_vip: parseInt(e.target.value) || 5 }))}
                        required
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : (editingRoute ? 'Update Route' : 'Create Route')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Car className="h-5 w-5" />
                  Transfer Routes Management
                </CardTitle>
                <CardDescription>
                  Select an airport to manage its transfer routes and pricing
                </CardDescription>
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="outline"
                  onClick={exportTemplate}
                  className="flex items-center gap-2"
                  size="sm"
                >
                  <Download className="h-4 w-4" />
                  Download Template
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={importFromExcel}
                  className="hidden"
                  disabled={importing}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="flex items-center gap-2"
                  size="sm"
                >
                  <Upload className="h-4 w-4" />
                  {importing ? 'Importing...' : 'Import from Excel'}
                </Button>
              </div>
            </div>
            {importing && (
              <div className="flex items-center gap-2 mt-4">
                <FileSpreadsheet className="h-4 w-4" />
                <Progress value={importProgress} className="flex-1 max-w-xs" />
                <span className="text-sm text-muted-foreground">{Math.round(importProgress)}%</span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <Tabs value={selectedAirport} onValueChange={(value) => setSelectedAirport(value as typeof selectedAirport)}>
              <TabsList className="grid w-full grid-cols-7 gap-2">
                {airports.map((airport) => (
                  <TabsTrigger key={airport.value} value={airport.value} className="text-xs">
                    {airport.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {airports.map((airport) => (
                <TabsContent key={airport.value} value={airport.value} className="space-y-4">
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Travel Area</TableHead>
                          <TableHead>Display Name</TableHead>
                          <TableHead>Standard Price</TableHead>
                          <TableHead>VIP Price</TableHead>
                          <TableHead>Max Pax (Std/VIP)</TableHead>
                          <TableHead>Extra Cost (Std/VIP)</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-6">
                              Loading routes...
                            </TableCell>
                          </TableRow>
                        ) : routes.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-6">
                              No routes found for {airport.label}
                            </TableCell>
                          </TableRow>
                        ) : (
                          routes.map((route) => (
                            <TableRow key={route.id}>
                              <TableCell className="font-medium">{route.travel_area}</TableCell>
                              <TableCell>{route.display_name}</TableCell>
                              <TableCell>${route.price_standard}</TableCell>
                              <TableCell>${route.price_vip}</TableCell>
                              <TableCell>{route.max_passenger_standard}/{route.max_passenger_vip}</TableCell>
                              <TableCell>${route.per_pax}/${route.per_pax_vip}</TableCell>
                              <TableCell className="text-right space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(route)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(route.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminTransfers;